import { CheckCircle, Terminal, XCircle } from 'lucide-react'

import { cn } from '@/lib/utils'

import { isRawResult, useExecutionOptional } from './execution-context'

export function OutputPane() {
  const executionContext = useExecutionOptional()

  const result = executionContext?.result ?? null
  const error = executionContext?.error ?? null
  const isExecuting = executionContext?.isExecuting ?? false

  if (isExecuting) {
    return (
      <div className="text-muted-foreground flex h-full flex-col items-center justify-center p-4 text-center">
        <div className="border-primary mb-4 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
        <p>Running your code...</p>
      </div>
    )
  }

  if (!result && !error) {
    return (
      <div className="text-muted-foreground flex h-full flex-col items-center justify-center p-4 text-center">
        <Terminal className="mb-4 h-12 w-12 opacity-50" />
        <p>Use the Run Code button to see the raw code output</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-full space-y-4 overflow-auto p-4">
        <div className="flex items-center gap-2">
          <XCircle className="h-5 w-5 text-red-500" />
          <h2 className="text-lg font-semibold text-red-500">Error</h2>
        </div>
        <div className="rounded-lg bg-red-500/10 p-4">
          <pre className="border-0 font-mono text-sm whitespace-pre-wrap text-red-700 dark:text-red-300">
            {error.message}
          </pre>
        </div>
      </div>
    )
  }

  if (result && isRawResult(result)) {
    const hasOutput = result.stdout || result.stderr
    const isSuccess = result.exitCode !== null && result.exitCode <= 3

    if (!hasOutput) {
      return (
        <div className="text-muted-foreground flex h-full flex-col items-center justify-center p-4 text-center">
          {isSuccess ? (
            <>
              <Terminal className="mb-4 h-12 w-12 opacity-50" />
              <p>Code ran successfully with no output</p>
            </>
          ) : (
            <>
              <XCircle className="mb-4 h-12 w-12 text-red-500" />
              <p className="font-medium text-red-600 dark:text-red-400">
                Execution failed with no output
              </p>
              <p className="mt-1 text-sm">
                Exit code: {result.exitCode ?? 'unknown'} (e.g. time limit
                exceeded or runtime error)
              </p>
            </>
          )}
        </div>
      )
    }

    return (
      <div className="h-full space-y-4 overflow-auto p-4">
        <div className="flex items-center gap-3">
          {isSuccess ? (
            <CheckCircle className="h-5 w-5 text-green-500" />
          ) : (
            <XCircle className="h-5 w-5 text-red-500" />
          )}
          <h2
            className={cn(
              'text-lg font-semibold',
              isSuccess ? 'text-green-500' : 'text-red-500',
            )}
          >
            {isSuccess ? 'Finished' : 'Error'}
          </h2>
        </div>

        {result.stdout && (
          <div className="space-y-2">
            <h3 className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
              Output
            </h3>
            <pre className="bg-muted/50 rounded-md p-3 font-mono text-sm whitespace-pre-wrap">
              {result.stdout}
            </pre>
          </div>
        )}

        {result.stderr && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-red-600">
              Errors
            </h3>
            <pre className="rounded-md bg-red-50 p-3 font-mono text-sm whitespace-pre-wrap text-red-800 dark:bg-red-950/30 dark:text-red-300">
              {result.stderr}
            </pre>
          </div>
        )}
      </div>
    )
  }

  // Non-raw result displayed here — show empty state (test results go to Test Results pane)
  return (
    <div className="text-muted-foreground flex h-full flex-col items-center justify-center p-4 text-center">
      <Terminal className="mb-4 h-12 w-12 opacity-50" />
      <p>Use the Run Code button to see the raw code output</p>
    </div>
  )
}
